import BENEFICIARY_SUBJECT_TYPE_KEY from '@Domain/Subjects/BeneficiarySubjectTypeKey';
import buildSubjectListPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectListPath';
import buildSubjectPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectPath';
import DeleteDocumentDialog from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/ConfirmationDialogs/DeleteDocumentDialog';
import DocumentDetailsContent from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/DocumentDetailsContent';
import DocumentsNotifications from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentsNotifications';
import { css } from '@emotion/react';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useSubjectAddressParams from '@UI/Pages/Beneficiaries/Hooks/Params/useSubjectAddressParams';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';

type RouteParams = {
	readonly document: string;
};

/**
 * The screen's own root: `flex: 1; min-height: 0` so it fills exactly what `AppMain` leaves below the
 * app header, without ever overflowing it (`AppMain.tsx`'s own `min-height: 0` is what makes that
 * possible). Carries no `overflow` itself: the actual scrolling happens one level down, in
 * `scrollStyle`, the single `overflow-y: auto` ancestor between here and the sticky tab bar.
 */
const rootStyle = css`
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
`;

/** The one scrolling container of the screen — header, progress card and the sticky section tabs all
 * sit inside it, so `.ui-kit-tab-view-nav-container`'s `position: sticky` has exactly this box to
 * stick against. */
const scrollStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1.25rem;
	flex: 1;
	min-height: 0;
	overflow-y: auto;
	padding: 0 1rem;
`;

/**
 * Mounted from `…/:dossier/documents/:document`. Validates both segments before
 * mounting `DocumentDetailsContent` — same motif as `InterviewDetails/index.tsx`: an invalid
 * dossier address redirects to the beneficiary list, an invalid `document` back to the dossier. Carries
 * no back link of its own: the guarded one lives in `DocumentVerificationHeader`, inside the content it
 * mounts below.
 */
const DocumentDetails: React.FC = () => {
	const navigate = useNavigate();
	const params = useParams<RouteParams>();

	const { subject } = useSubjectAddressParams();

	const documentId = params.document !== undefined ? Number(params.document) : Number.NaN;
	const isInvalidDocumentId = Number.isNaN(documentId);

	React.useEffect(() => {
		if (subject === null) {
			navigate(buildSubjectListPath(BENEFICIARY_SUBJECT_TYPE_KEY));
			return;
		}

		if (isInvalidDocumentId) {
			navigate(buildSubjectPath(subject));
		}
	}, [isInvalidDocumentId, subject, navigate]);

	return (
		<div css={rootStyle}>
			<div css={scrollStyle}>
				<ConditionalLoader data={isInvalidDocumentId ? null : subject}>
					{(validSubject) => (
						<DocumentDetailsContent subject={validSubject} documentId={documentId} />
					)}
				</ConditionalLoader>
			</div>
			<DocumentsNotifications />
			<ConditionalLoader data={isInvalidDocumentId ? null : subject}>
				<DeleteDocumentDialog />
			</ConditionalLoader>
		</div>
	);
};

export default DocumentDetails;
